import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import DocumentsSidebar from "./documents/DocumentsSidebar";
import DocGrid from "./documents/DocGrid";
import AiDrawer from "./documents/AiDrawer";
import { ShareDialog, VersionsDialog, DetailDialog } from "./documents/DocDialogs";
import { folderPath } from "./documents/util";

const MOBILE_VIEWS = [
  ["all", "All"], ["recent", "Recent"], ["favorites", "Favorites"],
  ["shared", "Shared"], ["archived", "Archived"], ["trash", "Trash"],
];

export default function Documents() {
  const [meta, setMeta] = useState(null);
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [view, setView] = useState("all");
  const [folderId, setFolderId] = useState(null);
  const [filters, setFilters] = useState({ q: "", category: "", status: "", sort: "recent", tag: "" });
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const [shareDoc, setShareDoc] = useState(null);
  const [versionsDoc, setVersionsDoc] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  const [aiDoc, setAiDoc] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [folderDialog, setFolderDialog] = useState(null); // {mode, parentId, folder}
  const [folderName, setFolderName] = useState("");

  const canVersion = meta?.features?.version_history;

  const loadMeta = useCallback(() => api.get("/documents/meta").then(({ data }) => setMeta(data)).catch(() => {}), []);
  const loadFolders = useCallback(() => api.get("/documents/folders").then(({ data }) => setFolders(data)).catch(() => {}), []);

  const loadDocs = useCallback(() => {
    setLoadingDocs(true);
    const done = (data) => { setDocs(data); setLoadingDocs(false); };
    if (view === "shared") {
      api.get("/documents/shared").then(({ data }) => done(data)).catch(() => setLoadingDocs(false));
      return;
    }
    const params = new URLSearchParams();
    params.set("view", ["favorites", "archived", "trash"].includes(view) ? view : "all");
    if (view === "folder" && folderId) params.set("folder_id", folderId);
    if (filters.q) params.set("q", filters.q);
    if (filters.category) params.set("category", filters.category);
    if (filters.status) params.set("status", filters.status);
    if (filters.tag) params.set("tag", filters.tag);
    params.set("sort", filters.sort);
    api.get(`/documents?${params}`).then(({ data }) => done(data)).catch(() => setLoadingDocs(false));
  }, [view, folderId, filters]);

  useEffect(() => { loadMeta(); loadFolders(); }, [loadMeta, loadFolders]);
  useEffect(() => { const t = setTimeout(loadDocs, 250); return () => clearTimeout(t); }, [loadDocs]);
  useEffect(() => { setSelected(new Set()); }, [view, folderId]);

  const tags = useMemo(() => Array.from(new Set(docs.flatMap((d) => d.tags || []))).slice(0, 20), [docs]);
  const breadcrumb = useMemo(() => (view === "folder" && folderId ? folderPath(folders, folderId) : []), [view, folderId, folders]);

  const refresh = useCallback(() => { loadDocs(); loadMeta(); }, [loadDocs, loadMeta]);

  // ---- Upload ----
  const onUpload = async (file) => {
    setUploading(true);
    const form = new FormData(); form.append("file", file);
    const params = new URLSearchParams({ category: "User Uploads" });
    if (view === "folder" && folderId) params.set("folder_id", folderId);
    try {
      await api.post(`/documents/upload?${params}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  // ---- Document actions ----
  const openDoc = async (id) => {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get(`/documents/${id}/signed-url`);
      const full = `${API.replace(/\/api$/, "")}${data.url}`;
      if (win) win.location.href = full; else window.open(full, "_blank");
    } catch (err) { if (win) win.close(); toast.error(formatApiError(err.response?.data?.detail) || "Could not open document"); }
  };
  const favorite = async (doc) => {
    setDocs((d) => d.map((x) => x.doc_id === doc.doc_id ? { ...x, favorite: !x.favorite } : x));
    try { await api.patch(`/documents/${doc.doc_id}`, { favorite: !doc.favorite }); } catch { refresh(); }
  };
  const copyDoc = async (id) => { try { await api.post(`/documents/${id}/copy`); toast.success("Duplicated"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Copy failed"); } };
  const deleteDoc = async (id, permanent) => {
    try { await api.delete(`/documents/${id}${permanent ? "?permanent=true" : ""}`); toast.success(permanent ? "Deleted permanently" : "Moved to trash"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  const restoreDoc = async (id) => { try { await api.post(`/documents/${id}/restore`); toast.success("Restored"); refresh(); } catch (e) { toast.error("Restore failed"); } };
  const openAi = (doc) => { setAiDoc(doc); setAiOpen(true); };

  // ---- Selection & bulk ----
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkActions = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const body = { doc_ids: ids, action };
    if (action === "favorite") body.value = "true";
    try { await api.post("/documents/bulk", body); toast.success(`${ids.length} updated`); setSelected(new Set()); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk action failed"); }
  };

  // ---- Folders ----
  const selectView = (v) => { setView(v); setFolderId(null); };
  const selectFolder = (id) => { setView("folder"); setFolderId(id); };
  const openFolderDialog = (parentId) => { setFolderDialog({ mode: "new", parentId }); setFolderName(""); };
  const folderCtx = async (action, node) => {
    if (action === "new") { setFolderDialog({ mode: "new", parentId: node.folder_id }); setFolderName(""); }
    else if (action === "rename") { setFolderDialog({ mode: "rename", folder: node }); setFolderName(node.name); }
    else if (action === "duplicate") { try { await api.post(`/documents/folders/${node.folder_id}/duplicate`); toast.success("Folder duplicated"); loadFolders(); } catch { toast.error("Could not duplicate"); } }
    else if (action === "delete") {
      if (!window.confirm(`Delete "${node.name}"? Documents inside move to root; subfolders are removed.`)) return;
      try { await api.delete(`/documents/folders/${node.folder_id}`); toast.success("Folder deleted"); if (folderId === node.folder_id) selectView("all"); loadFolders(); refresh(); }
      catch { toast.error("Could not delete"); }
    }
  };
  const submitFolder = async () => {
    const name = folderName.trim();
    if (!name) return;
    try {
      if (folderDialog.mode === "new") await api.post("/documents/folders", { name, parent_id: folderDialog.parentId });
      else await api.patch(`/documents/folders/${folderDialog.folder.folder_id}`, { name });
      toast.success(folderDialog.mode === "new" ? "Folder created" : "Folder renamed");
      setFolderDialog(null); loadFolders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  // ---- Drag & drop ----
  const isDescendant = (candidateChild, ancestorId) => {
    const map = Object.fromEntries(folders.map((f) => [f.folder_id, f]));
    let cur = map[candidateChild];
    while (cur) { if (cur.folder_id === ancestorId) return true; cur = cur.parent_id ? map[cur.parent_id] : null; }
    return false;
  };
  const onDropItem = async (e, targetFolderId) => {
    const docId = e.dataTransfer.getData("application/x-fv-doc");
    const dragFolderId = e.dataTransfer.getData("application/x-fv-folder");
    if (docId) {
      try { await api.patch(`/documents/${docId}`, { folder_id: targetFolderId }); toast.success("Moved"); refresh(); }
      catch { toast.error("Could not move document"); }
    } else if (dragFolderId && dragFolderId !== targetFolderId) {
      if (targetFolderId && isDescendant(targetFolderId, dragFolderId)) { toast.error("Can't move a folder into itself"); return; }
      try { await api.patch(`/documents/folders/${dragFolderId}`, { parent_id: targetFolderId }); toast.success("Folder moved"); loadFolders(); }
      catch { toast.error("Could not move folder"); }
    }
  };

  const rowHandlers = {
    onToggleSelect: toggleSelect, onOpen: openDoc, onAi: openAi, onShare: setShareDoc,
    onVersions: setVersionsDoc, onDetail: setDetailDoc, onFavorite: favorite, onCopy: copyDoc,
    onDelete: deleteDoc, onRestore: restoreDoc,
  };

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Manage" title="Document Library"
        subtitle="Your enterprise document vault — organize, version, share, and analyze with AI." />

      {/* Mobile view selector */}
      <div className="lg:hidden flex gap-1.5 overflow-x-auto pb-2 mb-4 mt-6" data-testid="mobile-views">
        {MOBILE_VIEWS.map(([k, label]) => (
          <button key={k} onClick={() => selectView(k)} data-testid={`mview-${k}`}
            className={`shrink-0 text-xs px-3 py-1.5 rounded-full ${view === k ? "fv-gold-gradient text-[#0A0A0B] font-medium" : "bg-white/5 text-[#A1A1AA]"}`}>{label}</button>
        ))}
      </div>

      <div className="flex gap-6 mt-2 lg:mt-8">
        <DocumentsSidebar
          view={view} folders={folders} selectedFolderId={folderId} meta={meta} tags={tags} activeTag={filters.tag}
          onSelectView={selectView} onSelectFolder={selectFolder} onSelectTag={(t) => setFilters((f) => ({ ...f, tag: t }))}
          onFolderCtx={folderCtx} onDropItem={onDropItem} onNewFolder={openFolderDialog} />

        {loadingDocs && docs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-24 text-[#71717A]" data-testid="docs-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <DocGrid
            docs={docs} meta={meta} view={view} breadcrumb={breadcrumb} selected={selected}
            bulkActions={bulkActions} filters={filters} setFilters={setFilters}
            uploading={uploading} onUpload={onUpload} onClearFolder={() => selectView("all")}
            onCrumb={selectFolder} rowHandlers={rowHandlers}
            onSelectAll={() => setSelected(new Set(docs.map((d) => d.doc_id)))} onClearSelect={() => setSelected(new Set())} />
        )}
      </div>

      <ShareDialog doc={shareDoc} onClose={() => setShareDoc(null)} />
      <VersionsDialog doc={versionsDoc} canVersion={canVersion} onClose={() => setVersionsDoc(null)} onChanged={refresh} />
      <DetailDialog doc={detailDoc} meta={meta} folders={folders} onClose={() => setDetailDoc(null)} onSaved={refresh} />
      <AiDrawer doc={aiDoc} open={aiOpen} onClose={() => setAiOpen(false)} />

      <Dialog open={!!folderDialog} onOpenChange={(o) => !o && setFolderDialog(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm" data-testid="folder-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">{folderDialog?.mode === "rename" ? "Rename folder" : "New folder"}</DialogTitle></DialogHeader>
          <input autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitFolder()} data-testid="folder-name-input" placeholder="Folder name"
            className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
          <DialogFooter>
            <Button onClick={submitFolder} data-testid="folder-save-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
              {folderDialog?.mode === "rename" ? "Rename" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
